import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Input, Modal, Row, Select, Table, Tag, message } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useLazyQuery, useMutation } from '@apollo/client';
import TablePaginationBar from 'components/TablePaginationBar';
import { HasResource } from 'components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';
import {
    DISPATCH_EVENT_TASKS,
    DispatchEventTaskFilter,
    DispatchEventTaskItem,
    DispatchEventTasksResponse,
} from '../graphql/queries/dispatchToolQueries';
import OrderFieldUpdateModal, { OrderFieldUpdateTarget } from './OrderFieldUpdateModal';
import {
    ARCHIVE_DISPATCH_EVENT_TASK,
    ArchiveDispatchEventTaskResponse,
    CANCEL_ARCHIVE_DISPATCH_EVENT_TASK,
    CancelArchiveDispatchEventTaskResponse,
    DELETE_DISPATCH_EVENT_TASK_ORDER_HISTORIES,
    DELETE_DISPATCH_EVENT_TASK_ORDERS,
    DeleteDispatchEventTaskOrderHistoriesResponse,
    DeleteDispatchEventTaskOrdersResponse,
} from '../graphql/mutations/dispatchToolMutations';
import {
    ARCHIVE_STATE,
    ARCHIVE_STATE_OPTIONS,
    COMPLETED_OPTIONS,
    DEFAULT_PAGINATION,
    PAGE_SIZE_OPTIONS,
    parseIdInput,
    renderArchiveState,
    renderDateTime,
    renderFlag,
} from './dispatchToolShared';

const OPERATE_TYPE_MAP: Record<number, { label: string; color: string }> = {
    1: { label: '发车', color: 'blue' },
    2: { label: '到达', color: 'green' },
    3: { label: '卸车', color: 'orange' },
    4: { label: '卸车LogScan', color: 'purple' },
    5: { label: '发运Tender', color: 'cyan' },
    6: { label: '装车移出', color: 'magenta' },
};

type FilterValues = {
    id?: string;
    taskId?: string;
    bagId?: string;
    orderId?: string;
    operateType?: number;
    isCompleted?: number;
    hasException?: number;
    isOrderCleaned?: number;
};

type Props = {
    /** 查看明细：archived=true 表示明细已归档，应跳归档 Tab */
    onViewOrders: (dispatchEventTaskId: number, archived: boolean) => void;
};

const DispatchEventTaskTab: React.FC<Props> = ({ onViewOrders }) => {
    const [form] = Form.useForm<FilterValues>();
    const [activeFilter, setActiveFilter] = useState<DispatchEventTaskFilter>({});
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    // 操作列「更新字段」弹窗：按 dispatchEventTaskId 批量更新明细表所选字段
    const [updateTarget, setUpdateTarget] = useState<OrderFieldUpdateTarget | null>(null);
    const [runQuery, { data, loading }] = useLazyQuery<DispatchEventTasksResponse>(DISPATCH_EVENT_TASKS, {
        fetchPolicy: 'network-only',
        onError: (e) => message.error(e.message),
    });
    const [runArchive] = useMutation<ArchiveDispatchEventTaskResponse>(ARCHIVE_DISPATCH_EVENT_TASK, {
        onError: (e) => message.error(e.message),
    });
    const [runDeleteOrders] = useMutation<DeleteDispatchEventTaskOrdersResponse>(DELETE_DISPATCH_EVENT_TASK_ORDERS, {
        onError: (e) => message.error(e.message),
    });
    const [runCancelArchive] = useMutation<CancelArchiveDispatchEventTaskResponse>(CANCEL_ARCHIVE_DISPATCH_EVENT_TASK, {
        onError: (e) => message.error(e.message),
    });
    const [runDeleteHistories] = useMutation<DeleteDispatchEventTaskOrderHistoriesResponse>(
        DELETE_DISPATCH_EVENT_TASK_ORDER_HISTORIES,
        { onError: (e) => message.error(e.message) },
    );

    const doDeleteHistories = async (record: DispatchEventTaskItem) => {
        const result = await runDeleteHistories({ variables: { dispatchEventTaskId: record.id } });
        const payload = result.data?.opsToolDeleteDispatchEventTaskOrderHistories;
        if (!payload) return;
        if (!payload.success) {
            message.error(payload.message);
            return;
        }
        message.success(`删除归档成功，共 ${payload.updatedCount ?? 0} 条明细`);
        setPagination((prev) => ({ ...prev }));
    };

    const handleDeleteHistories = (record: DispatchEventTaskItem) => {
        Modal.confirm({
            title: '风险操作确认',
            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
            okText: '确认删除归档',
            okButtonProps: { danger: true },
            content: `即将物理删除调度任务 ${record.id} 在归档表中的全部订单明细——不会搬回在线表，删除后不可恢复，且该任务将进入「归档已删除」终态，不再允许归档/取消归档/删除明细/更新字段，确认继续？`,
            onOk: () => {
                // 物理删除不可恢复，双重确认
                Modal.confirm({
                    title: '最终确认',
                    icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
                    okText: '我已知晓，确认删除归档',
                    okButtonProps: { danger: true },
                    content: `再次确认：调度任务 ${record.id} 的归档明细删除后无法恢复，确定执行？`,
                    onOk: () => doDeleteHistories(record),
                });
            },
        });
    };

    const handleCancelArchive = (record: DispatchEventTaskItem) => {
        Modal.confirm({
            title: '风险操作确认',
            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
            okText: '确认取消归档',
            okButtonProps: { danger: true },
            content: `即将把调度任务 ${record.id} 的全部订单明细从归档表搬回在线表并清除归档标记；若该任务仍满足自动归档条件（已完成且超保留天数），当晚定时任务会再次归档，确认继续？`,
            onOk: async () => {
                const result = await runCancelArchive({ variables: { dispatchEventTaskId: record.id } });
                const payload = result.data?.opsToolCancelArchiveDispatchEventTask;
                if (!payload) return;
                if (!payload.success) {
                    message.error(payload.message);
                    return;
                }
                message.success(`取消归档成功，恢复 ${payload.updatedCount ?? 0} 条明细`);
                setPagination((prev) => ({ ...prev }));
            },
        });
    };

    const doDeleteOrders = async (record: DispatchEventTaskItem) => {
        const result = await runDeleteOrders({ variables: { dispatchEventTaskId: record.id } });
        const payload = result.data?.opsToolDeleteDispatchEventTaskOrders;
        if (!payload) return;
        if (!payload.success) {
            message.error(payload.message);
            return;
        }
        message.success(`删除成功，共 ${payload.updatedCount ?? 0} 条明细`);
        setPagination((prev) => ({ ...prev }));
    };

    const handleDeleteOrders = (record: DispatchEventTaskItem) => {
        Modal.confirm({
            title: '风险操作确认',
            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
            okText: '确认删除',
            okButtonProps: { danger: true },
            content: `即将物理删除调度任务 ${record.id} 的全部在线订单明细——不会搬运到归档表，删除后不可恢复，确认继续？`,
            onOk: () => {
                // 物理删除不可恢复，双重确认
                Modal.confirm({
                    title: '最终确认',
                    icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
                    okText: '我已知晓，确认删除',
                    okButtonProps: { danger: true },
                    content: `再次确认：调度任务 ${record.id} 的在线明细删除后无法恢复，确定执行？`,
                    onOk: () => doDeleteOrders(record),
                });
            },
        });
    };

    const handleArchive = (record: DispatchEventTaskItem) => {
        Modal.confirm({
            title: '风险操作确认',
            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
            okText: '确认归档',
            okButtonProps: { danger: true },
            content: `即将把调度任务 ${record.id} 的全部订单明细搬运到归档表并从在线表删除（任务本身保留），该操作不可自动回滚，确认继续？`,
            onOk: async () => {
                const result = await runArchive({ variables: { dispatchEventTaskId: record.id } });
                const payload = result.data?.opsToolArchiveDispatchEventTask;
                if (!payload) return;
                if (!payload.success) {
                    message.error(payload.message);
                    return;
                }
                message.success(`归档成功，共 ${payload.updatedCount ?? 0} 条明细`);
                // 按当前条件重查，刷新「明细已归档」列
                setPagination((prev) => ({ ...prev }));
            },
        });
    };

    useEffect(() => {
        runQuery({
            variables: {
                filter: activeFilter,
                pageRequest: { page: pagination.current, size: pagination.pageSize },
            },
        });
    }, [activeFilter, pagination, runQuery]);

    const handleSearch = () => {
        const values = form.getFieldsValue();
        const id = parseIdInput(values.id);
        const taskId = parseIdInput(values.taskId);
        const bagId = parseIdInput(values.bagId);
        const orderId = parseIdInput(values.orderId);
        if (id === null || taskId === null || bagId === null || orderId === null) {
            message.warning('ID 必须是纯数字');
            return;
        }
        setPagination((prev) => ({ ...prev, current: 1 }));
        setActiveFilter({
            id,
            taskId,
            bagId,
            orderId,
            operateType: values.operateType,
            isCompleted: values.isCompleted,
            hasException: values.hasException,
            isOrderCleaned: values.isOrderCleaned,
        });
    };

    const columns: ColumnsType<DispatchEventTaskItem> = [
        { title: 'ID', dataIndex: 'id', width: 90 },
        { title: '任务ID', dataIndex: 'taskId', width: 90 },
        { title: '途经点ID', dataIndex: 'taskWaypointId', width: 100 },
        { title: '包牌ID', dataIndex: 'bagId', width: 90, render: (v) => v ?? '-' },
        { title: '订单ID', dataIndex: 'orderId', width: 100, render: (v) => v ?? '-' },
        {
            title: '操作类型',
            dataIndex: 'operateType',
            width: 110,
            render: (v: number) => {
                const item = OPERATE_TYPE_MAP[v];
                return item ? <Tag color={item.color}>{item.label}</Tag> : <Tag>{v}</Tag>;
            },
        },
        { title: '已填充明细', dataIndex: 'isOrderFilled', width: 100, render: (v: number) => renderFlag(v) },
        { title: '已完成', dataIndex: 'isCompleted', width: 80, render: (v: number) => renderFlag(v) },
        { title: '有异常', dataIndex: 'hasException', width: 80, render: (v: number) => renderFlag(v, true) },
        { title: '归档状态', dataIndex: 'isOrderCleaned', width: 110, render: (v: number | null) => renderArchiveState(v) },
        { title: '操作人ID', dataIndex: 'operatorAccountId', width: 100 },
        { title: '创建时间', dataIndex: 'createdAt', width: 160, render: renderDateTime },
        { title: '更新时间', dataIndex: 'updatedAt', width: 160, render: renderDateTime },
        {
            title: '操作',
            key: 'action',
            width: 370,
            fixed: 'right',
            render: (_, record) => {
                // 三态：0-未归档（在线表有明细）1-已归档（归档表有明细）2-归档已删除（两表均空，终态，仅可查看）
                const notArchived = record.isOrderCleaned === ARCHIVE_STATE.NONE;
                const archived = record.isOrderCleaned === ARCHIVE_STATE.ARCHIVED;
                const archiveDeleted = record.isOrderCleaned === ARCHIVE_STATE.DELETED;
                return (
                    <>
                        <Button
                            type="link"
                            size="small"
                            disabled={archiveDeleted}
                            onClick={() => onViewOrders(record.id, archived)}
                        >
                            查看明细
                        </Button>
                        <HasResource resourceCodes={[ResourceCode.menu.OPS_TOOL_TMS]}>
                            <Button
                                type="link"
                                size="small"
                                disabled={record.isCompleted === 1 || !notArchived}
                                onClick={() => setUpdateTarget({ kind: 'task', dispatchEventTaskId: record.id })}
                            >
                                更新字段
                            </Button>
                            <Button
                                type="link"
                                size="small"
                                disabled={record.isCompleted !== 1 || !notArchived}
                                onClick={() => handleArchive(record)}
                            >
                                归档
                            </Button>
                            <Button
                                type="link"
                                size="small"
                                disabled={!archived}
                                onClick={() => handleCancelArchive(record)}
                            >
                                取消归档
                            </Button>
                            <Button
                                type="link"
                                size="small"
                                danger
                                disabled={record.isOrderFilled !== 1 || record.isCompleted === 1 || !notArchived}
                                onClick={() => handleDeleteOrders(record)}
                            >
                                删除明细
                            </Button>
                            <Button
                                type="link"
                                size="small"
                                danger
                                disabled={!archived}
                                onClick={() => handleDeleteHistories(record)}
                            >
                                删除归档
                            </Button>
                        </HasResource>
                    </>
                );
            },
        },
    ];

    const rows = data?.dispatchEventTasks.content ?? [];
    const totalElements = data?.dispatchEventTasks.totalElements ?? 0;

    return (
        <div>
            <Form form={form}>
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item name="id" label="主键ID">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="taskId" label="任务ID">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="bagId" label="包牌ID">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="orderId" label="订单ID">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="operateType" label="操作类型">
                            <Select
                                allowClear
                                placeholder="全部（无索引，建议配合ID条件查询）"
                                options={Object.entries(OPERATE_TYPE_MAP).map(([value, item]) => ({
                                    value: Number(value),
                                    label: item.label,
                                }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="isCompleted" label="完成状态">
                            <Select allowClear placeholder="全部" options={COMPLETED_OPTIONS} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="hasException" label="是否异常">
                            <Select
                                allowClear
                                placeholder="全部（无索引，建议配合ID条件查询）"
                                options={[
                                    { value: 1, label: '是' },
                                    { value: 0, label: '否' },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="isOrderCleaned" label="归档状态">
                            <Select allowClear placeholder="全部" options={ARCHIVE_STATE_OPTIONS} />
                        </Form.Item>
                    </Col>
                    <Col span={8} className="ml-auto text-right">
                        <Form.Item>
                            <Button className="mr-2" onClick={() => form.resetFields()}>
                                重置
                            </Button>
                            <Button type="primary" loading={loading} onClick={handleSearch}>
                                查询
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <Table<DispatchEventTaskItem>
                rowKey="id"
                size="small"
                loading={loading}
                columns={columns}
                dataSource={rows}
                pagination={false}
                scroll={{ x: 1400 }}
                className="mb-4"
            />
            <TablePaginationBar
                total={totalElements}
                current={pagination.current}
                pageSize={pagination.pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onChange={(page, size) => setPagination({ current: page, pageSize: size })}
            />
            <OrderFieldUpdateModal
                target={updateTarget}
                onClose={() => setUpdateTarget(null)}
                onSuccess={() => setPagination((prev) => ({ ...prev }))}
            />
        </div>
    );
};

export default DispatchEventTaskTab;
